import React from 'react';
import { Category, TestResult } from '../../types';

export interface TestManagerStatsBannerProps {
  categories: Category[];
  currentTest: TestResult;
  isStatsCollapsed: boolean;
  onStatsCollapsedChange: (collapsed: boolean) => void;
  isTestMenuOpen: boolean;
  onTestMenuOpenChange: (open: boolean) => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
  onEditCSV?: () => void;
  onEditTest: () => void;
  onDeleteTest: () => void;
}

const countScenarios = (categories: Category[], filter?: (s: { Status?: string; isDeleted?: string }) => boolean) =>
  categories.reduce(
    (acc, cat) => acc + cat.scenarios.filter((s) => s.isDeleted !== 'true' && (!filter || filter(s))).length,
    0
  );

export const TestManagerStatsBanner: React.FC<TestManagerStatsBannerProps> = ({
  categories,
  currentTest,
  isStatsCollapsed,
  onStatsCollapsedChange,
  isTestMenuOpen,
  onTestMenuOpenChange,
  menuRef,
  onEditCSV,
  onEditTest,
  onDeleteTest,
}) => {
  const total = countScenarios(categories);
  const passed = countScenarios(categories, (s) => s.Status === 'Passed');
  const failed = countScenarios(categories, (s) => s.Status === 'Failed');
  const blocked = countScenarios(categories, (s) => s.Status === 'Blocked');
  const completed = countScenarios(categories, (s) => !!(s.Status && s.Status !== 'Not Tested'));
  const progressPercent = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-8 mb-8 transform transition-all duration-200 hover:shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Statystyki testów
          </h2>
          <button
            onClick={() => onStatsCollapsedChange(!isStatsCollapsed)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-600 rounded-lg transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isStatsCollapsed ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"} />
            </svg>
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {currentTest.filename}
          </div>
          <div className={`text-sm px-3 py-1 rounded-full flex items-center gap-2 ${
            currentTest.metadata?.isFullRegression ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'bg-purple-50 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
          }`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            {currentTest.metadata?.isFullRegression ? 'Pełne testy regresyjne' : 'Wybrane kategorie testów'}
          </div>
          {currentTest.metadata?.version && (
            <div className="text-sm bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Wersja: {currentTest.metadata.version}
            </div>
          )}
          <div className="relative" ref={menuRef as React.RefObject<HTMLDivElement>}>
            <button
              onClick={() => onTestMenuOpenChange(!isTestMenuOpen)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-600 rounded-lg transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {isTestMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                <button
                  onClick={() => { onEditCSV?.(); onTestMenuOpenChange(false); }}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  title="Edycja pliku CSV ze scenariuszami"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Edytuj plik CSV
                </button>
                <button
                  onClick={() => { onEditTest(); onTestMenuOpenChange(false); }}
                  className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edytuj test
                </button>
                <button
                  onClick={() => { onDeleteTest(); onTestMenuOpenChange(false); }}
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Usuń test
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isStatsCollapsed && (
        <>
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-gray-50 dark:bg-slate-600/50 rounded-xl p-6 text-center transform transition-all duration-200 hover:scale-105">
              <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{total}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Wszystkie testy</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-6 text-center transform transition-all duration-200 hover:scale-105">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{passed}</div>
              <div className="text-sm text-green-600 dark:text-green-400">Zaliczone</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/30 rounded-xl p-6 text-center transform transition-all duration-200 hover:scale-105">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-2">{failed}</div>
              <div className="text-sm text-red-600 dark:text-red-400">Niezaliczone</div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-xl p-6 text-center transform transition-all duration-200 hover:scale-105">
              <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">{blocked}</div>
              <div className="text-sm text-yellow-600 dark:text-yellow-400">Zablokowane</div>
            </div>
          </div>
          <div className="mt-8 bg-gray-50 dark:bg-slate-600/50 rounded-xl p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Szczegóły testu
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">System</div>
                  <div className="font-medium text-gray-900 dark:text-white">{currentTest.metadata?.systemName || 'Nie określono'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Środowisko</div>
                  <div className="font-medium text-gray-900 dark:text-white">{currentTest.metadata?.environment || 'Nie określono'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Data rozpoczęcia</div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {new Date(currentTest.metadata?.startDate).toLocaleString('pl-PL', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Platformy</div>
                  <div className="font-medium flex gap-1 flex-wrap text-gray-900 dark:text-white">
                    {(currentTest.metadata?.platforms || ['Nie określono']).map((platform) => (
                      <span key={platform} className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Tester</div>
                  <div className="font-medium text-gray-900 dark:text-white">{currentTest.metadata?.testerName || 'Nie określono'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white dark:bg-slate-600 p-3 rounded-lg">
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Wersja</div>
                  <div className="font-medium text-gray-900 dark:text-white">{currentTest.metadata?.version || 'Nie określono'}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="mt-8">
        <div className="w-full h-3 bg-gray-100 dark:bg-slate-600 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-3 text-center">
          Postęp: {progressPercent.toFixed(2)}%
        </div>
      </div>
    </div>
  );
};
